// src/pages/SettingsPage/components/WorkspaceSubscriptionSettings.tsx
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, Typography } from "@mui/material";
import React from "react";
import { NoteBlockContainer } from "../../note";
import { StaticHtmlBlock } from "../../staticHtmlBlock";
import { useAppOwner } from "../../../hook";
import {
  useSubscriptionStatus,
  useFetchStripeSubscription,
} from "../../../hook/settings";
import { formatDate } from "./utils/settings.utils"; // Adjusted path
import {
  activateSubscription,
  cancelSubscription,
  createSubscription,
} from "../../../api/payment";

export const WorkspaceSubscriptionSettings: React.FC = () => {
  const { user } = useAuth0();
  const email = user?.email!;
  const [appOwner] = useAppOwner();
  const {
    periodStart,
    periodEnd,
    renewing,
    canPayImmediately,
    canEnableRenewal,
    addPaymentMethodPrompt,
    canCancel,
    trialing,
    hasAccess,
    cancelAtPeriodEnd,
    active,
    canceled,
    workspaceOwnerPrompt,
    loading,
  } = useSubscriptionStatus();
  const fetchSubscription = useFetchStripeSubscription();

  const onEnableRenewal = () => {
    activateSubscription({ email }).then(() => {
      fetchSubscription();
    });
  };

  const onRenew = () => {
    createSubscription({ email }).then(() => {
      fetchSubscription();
    });
  };

  const onCancel = () => {
    cancelSubscription({ email }).then(() => {
      fetchSubscription();
    });
  };

  if (loading) {
    return (
      <NoteBlockContainer name="Subscription Status">
        <Typography>Loading subscription details...</Typography>
      </NoteBlockContainer>
    );
  }

  return (
    <>
      <NoteBlockContainer name="Subscription Status">
        {trialing && (
          <StaticHtmlBlock html="<p>You're on a free trial. Add a payment method before your trial ends to keep access.</p>" />
        )}
        {active && !trialing && (
          <StaticHtmlBlock html="<p>Your subscription is active. You can cancel at any time — access continues until the end of the current period.</p>" />
        )}
        {canceled && (
          <StaticHtmlBlock html="<p>Your subscription is inactive. Renew below to restore access.</p>" />
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Status Indicators */}
          {trialing && <Typography variant="h6">Status: Free Trial</Typography>}
          {active && !trialing && (
            <Typography variant="h6">Status: Active</Typography>
          )}
          {canceled && <Typography variant="h6">Status: Inactive</Typography>}

          {/* Renewal/Cancelation Info */}
          {renewing && !trialing && (
            <Typography>Subscription will renew automatically.</Typography>
          )}
          {cancelAtPeriodEnd && (
            <Typography color="warning.main">
              Subscription will cancel at the end of the current period.
            </Typography>
          )}
          {addPaymentMethodPrompt && (
            <Typography color="error.main">
              Add a Payment Method to avoid cancelation.
            </Typography>
          )}

          {/* Period Info */}
          {hasAccess && (
            <>
              <Typography>
                <strong>Current Period Start:</strong>{" "}
                {formatDate({ timestamp: periodStart })}
              </Typography>
              <Typography>
                <strong>Current Period End:</strong>{" "}
                {formatDate({ timestamp: periodEnd })}
              </Typography>
            </>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            {canCancel && (
              <Button onClick={onCancel} variant="outlined" color="warning">
                Cancel At Period End
              </Button>
            )}
            {canEnableRenewal && (
              <Button onClick={onEnableRenewal} variant="contained">
                Re-enable Renewal
              </Button>
            )}
            {canPayImmediately && (
              <Button onClick={onRenew} variant="contained">
                Renew Canceled Subscription
              </Button>
            )}
          </Box>

          {/* Owner Prompt */}
          {workspaceOwnerPrompt && (
            <Typography sx={{ mt: 2 }}>
              Contact workspace admin ({appOwner}) to manage the subscription.
            </Typography>
          )}
        </Box>
      </NoteBlockContainer>
    </>
  );
};
